import { BasicButton, BasicModal, Loading } from "@/components";
import env from "@/env.config";
import useProfile from "@/hooks/useProfile";
import { SUBSCRIPTION_STATUS } from "@/libraries/constant";
import { isAuthenticating, isWebview } from "@/libraries/helper";
import { subcsrition } from "@/redux/reducers/auth";
import { AuthWebviewSchema } from "@/schemas";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import CancelIcon from "@mui/icons-material/Cancel";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { capitalize } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

export const Route = createFileRoute("/manage-subscription")({
	validateSearch: zodSearchValidator(AuthWebviewSchema),
	beforeLoad: async () => {
		if (!isAuthenticating()) {
			throw redirect({
				to: "/",
				replace: true,
			});
		}
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation(["settings", "auth", "nav"]);
	const dispatch = useDispatch();
	const navigate = Route.useNavigate();
	const { profile: userData, isFreeTrial, isLoading } = useProfile();
	const [modalOpen, setModalOpen] = useState(false);

	function formatDate(dateString: string) {
		const date = new Date(dateString);
		const formattedDate = date.toLocaleDateString("en-GB", {
			day: "numeric",
			month: "long",
			year: "numeric",
		});
		return formattedDate.replace(/(\d+)(\s\w+\s)(\d+)/, "$1 $2 $3");
	}

	const hasWebview = isWebview();

	const handleBack = useCallback(() => {
		if (isWebview()) {
			window.location.href = env.baseApiUrl;
		} else {
			navigate({
				to: "/dashboard/accounts",
			});
		}
	}, [navigate]);

	// submit cancel subscription
	const handleSubmit = useCallback(() => {
		dispatch(
			subcsrition({
				callback: () => {
					setModalOpen(false);
					window.location.reload();
				},
			}),
		);
	}, [dispatch]);

	const isProPlan =
		!!userData?.subscription_status &&
		!userData?.subscription_plan?.cancel_at_period_end &&
		userData.subscription_status === SUBSCRIPTION_STATUS.PRO;

	const subscription = useMemo(() => {
		if (isFreeTrial || userData?.subscription_plan?.cancel_at_period_end)
			return t("settings:freePlan");
		if (userData?.subscription_status && userData?.subscription_plan) {
			return t("auth:cookieCredentials.planBillingLine", {
				plan: capitalize(userData.subscription_status),
				billing: capitalize(userData.subscription_plan?.name),
			});
		}
		return "-";
	}, [userData, isFreeTrial, t]);

	if (isLoading) {
		return <Loading status={isLoading} />;
	}

	return (
		<>
			<div className="container wrapper h-dvh bg-gray-light">
				{!hasWebview && (
					<AppBar
						position="fixed"
						className="!bg-white !text-dark !shadow-none"
					>
						<Toolbar variant="dense" className="wrapper w-full">
							<IconButton
								onClick={handleBack}
								edge="start"
								color="inherit"
								aria-label="backspace"
								className="text-2xl mr-2"
							>
								<KeyboardBackspaceIcon />
							</IconButton>
							<p className="text-sm text-dark font-semibold">
								{t("settings:manageSubscription.title")}
							</p>
						</Toolbar>
					</AppBar>
				)}
				<Box className={hasWebview ? "mt-0" : "mt-12"}>
					<h6 className="text-sm text-dark font-bold py-2 mb-1">
						{t("settings:manageSubscription.subscriptionDetails")}
					</h6>
					<Box className="bg-white h-12 flex justify-between gap-0.5 items-center rounded-xl py-2.5 px-4 mb-3">
						<p className="text-sm text-dark">
							{t("settings:manageSubscription.status")}
						</p>
						{!userData?.subscription_plan?.cancel_at_period_end &&
						userData?.subscription_status === "free_trial" ? (
							<button
								type="button"
								className="bg-danger-300 text-xs text-danger-100 rounded py-1 px-2"
							>
								{t("settings:manageSubscription.cancelled")}
							</button>
						) : !userData?.subscription_plan?.cancel_at_period_end ? (
							<button
								type="button"
								className="bg-primary text-xs text-white rounded py-1 px-2"
							>
								{t("settings:manageSubscription.active")}
							</button>
						) : (
							<button
								type="button"
								className="bg-danger-300 text-xs text-danger-100 rounded py-1 px-2"
							>
								{t("settings:manageSubscription.cancelled")}
							</button>
						)}
					</Box>
					<Box className="bg-white h-12 flex justify-between items-center rounded-xl py-3.5 px-4 mb-3">
						<p className="text-sm text-dark">
							{t("settings:manageSubscription.subscription")}
						</p>
						<p className="text-sm text-dark font-semibold">{subscription}</p>
					</Box>
					{!userData?.subscription_plan?.cancel_at_period_end &&
						userData?.subscription_status !== "free_trial" && (
							<Box className="bg-white h-12 flex justify-between items-center rounded-xl py-3.5 px-4 mb-3">
								<p className="text-sm text-dark">
									{t("settings:manageSubscription.billingAmount")}
								</p>
								<p className="text-sm text-dark font-semibold">
									RM{userData?.subscription_plan?.billing_amount ?? 0}
								</p>
							</Box>
						)}
					{!userData?.subscription_plan?.cancel_at_period_end &&
						userData?.subscription_status !== "free_trial" && (
							<Box className="bg-white h-12 flex justify-between items-center rounded-xl py-3.5 px-4 mb-3">
								<p className="text-sm text-dark">
									{t("settings:manageSubscription.nextBilling")}
								</p>
								<p className="text-sm text-dark font-semibold">
									{userData?.subscription_plan?.next_billing_on
										? formatDate(userData.subscription_plan.next_billing_on)
										: t("settings:manageSubscription.noNextBillingDate")}
								</p>
							</Box>
						)}
					{(userData?.subscription_plan?.cancel_at_period_end &&
						userData?.subscription_status !== "free_trial") ||
					(!userData?.subscription_plan?.cancel_at_period_end &&
						userData?.subscription_status === "free_trial") ? (
						<Box className="bg-white h-12 flex justify-between items-center rounded-xl py-3.5 px-4 mb-3">
							<p className="text-sm text-dark">
								{t("settings:manageSubscription.expiredOn")}
							</p>
							<p className="text-sm text-dark font-semibold">
								{userData?.subscription_plan?.expired_on
									? formatDate(userData?.subscription_plan.expired_on)
									: t("settings:manageSubscription.noExpirationDate")}
							</p>
						</Box>
					) : null}
					<div className="flex flex-col gap-3">
						{!isProPlan ? (
							<Link to={"/subscription"}>
								<BasicButton
									variant="contained"
									color="secondary"
									className="w-full !h-12 text-sm font-semibold !shadow-none mb-3"
									startIcon={<ArrowCircleUpIcon />}
								>
									{t("settings:manageSubscription.upgradePlan")}
								</BasicButton>
							</Link>
						) : null}
						{!userData?.subscription_plan?.cancel_at_period_end &&
						userData?.subscription_status === "free_trial" ? (
							<Link to="/subscription">
								<BasicButton
									variant="contained"
									color="secondary"
									className="w-full !h-12 text-sm font-semibold !shadow-none mb-3"
								>
									{t("settings:manageSubscription.reactivateSubscription")}
								</BasicButton>
							</Link>
						) : !userData?.subscription_plan?.cancel_at_period_end ? (
							<button
								type="button"
								onClick={() => setModalOpen(true)}
								className="w-full h-12 bg-white border-neutral-100 rounded-full"
							>
								<Box className="flex justify-center items-center gap-2">
									<CancelIcon className="text-base text-danger-300" />
									<span className="text-sm font-semibold text-danger-300">
										{t("settings:manageSubscription.cancelSubscription")}
									</span>
								</Box>
							</button>
						) : (
							<Link to={"/subscription"}>
								<BasicButton
									variant="contained"
									color="secondary"
									className="w-full !h-12 text-sm font-semibold !shadow-none mb-3"
								>
									{t("settings:manageSubscription.reactivateSubscription")}
								</BasicButton>
							</Link>
						)}
					</div>
					<p className="text-xs text-gray-100 mt-40 mb-3">
						{t("settings:manageSubscription.contractDetailsPrefix")}{" "}
						<Link to={"/term-service"}>
							<span className="text-primary font-semibold uppercase">
								{t("settings:manageSubscription.termsAndConditions")}
							</span>
						</Link>{" "}
						{t("settings:manageSubscription.contractDetailsSuffix")}
					</p>
					<a href="mailto:contact@medplanner.io">
						<BasicButton
							variant="contained"
							color="primary"
							className="w-full !border !border-solid !border-light-gray !shadow-none"
						>
							<Box className="flex gap-1">
								<SupportAgentIcon />
								<p className="text-base font-semibold">
									{t("settings:manageSubscription.contactSupport")}
								</p>
							</Box>
						</BasicButton>
					</a>
				</Box>
			</div>

			<BasicModal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
				<p className="text-base text-semibold text-dark text-center mb-3">
					<b>{t("settings:manageSubscription.cancelSubscriptionModalTitle")}</b>
				</p>
				<p className="text-sm text-gray-100 text-center mb-3">
					{t("settings:manageSubscription.cancelSubscriptionModalMessage")}{" "}
					<b>{formatDate(userData?.subscription_plan?.expired_on as string)}</b>
				</p>
				<BasicButton
					variant="contained"
					color="secondary"
					onClick={handleSubmit}
					className="w-full !h-12 text-sm !mb-2 !shadow-none"
				>
					{t("settings:manageSubscription.confirm")}
				</BasicButton>
				<BasicButton
					variant="outlined"
					color="secondary"
					onClick={() => setModalOpen(false)}
					className="w-full !h-12 text-sm !mb-2"
				>
					{t("settings:manageSubscription.cancel")}
				</BasicButton>
			</BasicModal>
		</>
	);
}
